import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Divider,
  IconButton,
  Paper,
} from "@mhome/ui";
import { useMediaQuery } from "@mui/material";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState } from "../global-state";
import MSwitch from "../common/switch";
import CloseIcon from "@mui/icons-material/Close";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const ConnectionSettingsDialog = ({ open, onClose, connection, onSave }) => {
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");
  const getComponentBgColor = useComponentBackgroundColor();

  const [active, setActive] = useState(false);
  const [preferredName, setPreferredName] = useState("");
  const [editingName, setEditingName] = useState(false);

  const [notificationSettings, setNotificationSettings] = useState({
    enabled: false,
    levels: {
      low: false,
      high: false,
      dialog: false,
    },
  });

  useEffect(() => {
    if (connection) {
      setActive(connection.active || false);
      setPreferredName(connection.preferredName || "");

      const levels = connection.notificationSetting?.levels || [];
      setNotificationSettings({
        enabled: connection.notificationSetting?.enabled || false,
        levels: {
          low: levels.includes("low"),
          high: levels.includes("high"),
          dialog: levels.includes("dialog"),
        },
      });
    }
  }, [connection]);

  const handleSave = () => {
    // Convert levels object back to array format
    const enabledLevels = Object.keys(notificationSettings.levels).filter(
      (level) => notificationSettings.levels[level]
    );

    const updatedConnection = {
      ...connection,
      preferredName: preferredName,
      active: active,
      notificationSetting: {
        enabled: notificationSettings.enabled,
        levels: enabledLevels,
      },
    };
    onSave(updatedConnection);
  };

  const handleLevelChange = (level) => (event) => {
    setNotificationSettings((prev) => ({
      ...prev,
      levels: {
        ...prev.levels,
        [level]: event.target.checked,
      },
    }));
  };

  const handleNameClick = () => {
    setEditingName(true);
  };

  const handleNameBlur = () => {
    setEditingName(false);
  };

  const handleNameKeyPress = (e) => {
    if (e.key === "Enter") {
      setEditingName(false);
    }
  };

  if (!connection) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isSmallScreen}
      maxWidth={false}
      PaperProps={{
        style: {
          width: isSmallScreen ? "100%" : "600px",
          maxWidth: isSmallScreen ? "100%" : "600px",
          minWidth: isSmallScreen ? "100%" : "600px",
          marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px`,
          marginBottom: isSmallScreen ? 0 : `${safeAreaInsets.bottom}px`,
          height: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${safeAreaInsets.top + safeAreaInsets.bottom}px)`,
          maxHeight: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${safeAreaInsets.top + safeAreaInsets.bottom}px)`,
          paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
          paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
          paddingRight: `${safeAreaInsets.right}px`,
        },
      }}
    >
      <DialogTitle
        style={{
          padding: "8px",
          gap: "8px",
          display: "flex",
          alignItems: "center",
          minHeight: "auto",
        }}
      >
        <IconButton onClick={onClose} style={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
        <Typography
          variant="h6"
          component="div"
          style={{ flex: 1, lineHeight: 1.2 }}
        >
          Connection Settings
        </Typography>
      </DialogTitle>

      <DialogContent style={{ paddingBottom: "8px" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            marginTop: "8px",
          }}
        >
          {/* Display Name */}
          <Paper
            variant="outlined"
            style={{
              padding: "16px",
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
              border: `1px solid hsl(var(--border))`,
            }}
          >
            <Typography
              variant="subtitle2"
              style={{ marginBottom: "16px", fontWeight: 500 }}
            >
              Display Name
            </Typography>
            <TextField
              value={preferredName}
              onChange={(e) => setPreferredName(e.target.value)}
              onBlur={handleNameBlur}
              onKeyPress={handleNameKeyPress}
              placeholder="Enter display name"
              variant="outlined"
              fullWidth
              size="small"
              autoFocus
            />
          </Paper>

          {/* Connection Status */}
          <Paper
            variant="outlined"
            style={{
              padding: "16px",
              backgroundColor: getComponentBgColor("hsl(var(--card))"),
              border: `1px solid hsl(var(--border))`,
            }}
          >
            <Typography
              variant="subtitle2"
              style={{ marginBottom: "16px", fontWeight: 500 }}
            >
              Connection Status
            </Typography>
            <div style={{ display: "flex", gap: "16px" }}>
              <MSwitch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                style={{ marginTop: "4px" }}
              />
              <div>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  Active Connection
                </Typography>
                <Typography
                  variant="body2"
                  className="text-muted-foreground"
                  style={{
                    marginTop: "4px",
                  }}
                >
                  Enable or disable this Telegram connection for receiving
                  messages
                </Typography>
              </div>
            </div>
          </Paper>

          {/* Notification Settings */}
          {active && (
            <Paper
              variant="outlined"
              style={{
                padding: "16px",
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                border: `1px solid hsl(var(--border))`,
              }}
            >
              <Typography
                variant="subtitle2"
                style={{ marginBottom: "16px", fontWeight: 500 }}
              >
                Notification Settings
              </Typography>

              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  marginBottom: notificationSettings.enabled ? "24px" : 0,
                }}
              >
                <MSwitch
                  checked={notificationSettings.enabled}
                  onChange={(e) =>
                    setNotificationSettings((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                  style={{ marginTop: "4px" }}
                />
                <div>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    Enable Notifications
                  </Typography>
                </div>
              </div>

              {notificationSettings.enabled && (
                <div>
                  <Typography
                    variant="subtitle2"
                    style={{ marginBottom: "12px", fontWeight: 500 }}
                  >
                    Notification Levels
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={notificationSettings.levels.low}
                          onChange={handleLevelChange("low")}
                          size="small"
                        />
                      }
                      label="Low Priority"
                      style={{ marginLeft: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={notificationSettings.levels.high}
                          onChange={handleLevelChange("high")}
                          size="small"
                        />
                      }
                      label="High Priority"
                      style={{ marginLeft: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={notificationSettings.levels.dialog}
                          onChange={handleLevelChange("dialog")}
                          size="small"
                        />
                      }
                      label="Dialog Messages"
                      style={{ marginLeft: 0 }}
                    />
                  </div>
                </div>
              )}
            </Paper>
          )}
        </div>
      </DialogContent>

      <DialogActions style={{ padding: "16px", gap: "8px" }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          style={{ boxShadow: "none" }}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectionSettingsDialog;
